// "Trading Balance" on Home > Overview: how much of the payable total is late and how much of the
// receivable total is waiting on paperwork, as two bars on one $ scale.
//
// Signs (theClearinghouse appBackend): org.eggs.account_balances sends BuyerBal = SUM(BalBuyer) over
// the member's purchases and SellerBal = SUM(BalSeller) over their sales, credits included. A buyer
// balance still owed is negative (trades_needing_payment selects BalBuyer < 0; Payables negates
// payable_list's `Balance`, which is BalBuyer, to show it positive), a seller balance due to the
// member positive (Receivables shows it as sent). So each side is read in its own owed direction
// (payables -1, receivables +1) and drawn as magnitudes: a net credit (a positive BuyerBal, the
// member overpaid; a negative SellerBal, the member owes on sales) is no bar, a part counts in the
// owed direction (one netting against it counts as 0), and a part is capped at the total.
//
// A port of the ECI mobile app's src/services/balanceAging.ts. Keep the two in step.

/**
 * @typedef {object} AgingSplit
 * @property {number} total What is owed, as a magnitude; 0 for nothing owed or a net credit (no bar).
 * @property {number} part The part that needs attention (late payables, receivables waiting on
 *   paperwork), 0..total.
 * @property {number} rest total - part.
 * @property {true} [unknown] Set when the part isn't known (its list failed to load): the bar is
 *   drawn undivided.
 */

/**
 * Which sign a balance has while it's owed: payables negative, receivables positive.
 * @typedef {1 | -1} Owed
 */

const cents = n => Math.round(n * 100) / 100;
const finite = n => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

const NONE = {total: 0, part: 0, rest: 0};

/**
 * Splits a signed balance total into the part made of `parts` (signed balances of some of the same
 * trades) and the rest, as magnitudes in the `owed` direction. A total under half a cent, or one
 * running the other way (a credit), is 0: no bar, and the row keeps its text total.
 *
 * @param {number} total
 * @param {number[]} parts
 * @param {Owed} owed
 * @returns {AgingSplit}
 */
export function agingSplit(total, parts, owed) {
  const magnitude = cents(finite(total) * owed);
  if (magnitude <= 0) {
    return NONE;
  }
  const toward = cents(parts.reduce((sum, p) => sum + finite(p), 0) * owed);
  const part = Math.min(Math.max(toward, 0), magnitude);
  return {total: magnitude, part, rest: cents(magnitude - part)};
}

/**
 * A total whose part isn't known: one undivided bar.
 *
 * The app keeps this private. The web exports it because it also draws Receivables undivided when
 * seller_account_needs fails to load; the app never draws with a failed needs list.
 *
 * @param {number} total
 * @param {Owed} owed
 * @returns {AgingSplit}
 */
export function unsplit(total, owed) {
  const split = agingSplit(total, [], owed);
  return split.total > 0 ? {...split, unknown: true} : split;
}

/**
 * Payables: the late part is what trades_needing_payment lists (14+ days past settlement). Null
 * when that list failed to load: nothing is claimed late or current then.
 *
 * @param {number} buyerBal
 * @param {{BalBuyer: number}[] | null} paymentDue
 * @returns {AgingSplit}
 */
export const payableAging = (buyerBal, paymentDue) =>
  paymentDue ? agingSplit(buyerBal, paymentDue.map(t => t.BalBuyer), -1) : unsplit(buyerBal, -1);

/**
 * Receivables: the waiting part is what seller_account_needs lists (no invoice or bill of lading).
 *
 * @param {number} sellerBal
 * @param {{BalSeller: number}[]} needs
 * @returns {AgingSplit}
 */
export const receivableAging = (sellerBal, needs) =>
  agingSplit(sellerBal, needs.map(t => t.BalSeller), 1);

/** @typedef {{x: number, width: number}} Segment */

/**
 * Where a bar's segments go on a track `width` wide where `scaleMax` fills it: left to right, a
 * `gap` between drawn segments, a zero segment not drawn (width 0) and every non-zero one at least
 * `minWidth`, wherever it falls, never past the track and never negative.
 *
 * @param {number[]} values
 * @param {number} scaleMax
 * @param {number} width
 * @param {number} [gap=2]
 * @param {number} [minWidth=2]
 * @returns {Segment[]}
 */
export function barSegments(values, scaleMax, width, gap = 2, minWidth = 2) {
  const track = Math.max(0, finite(width));
  const clean = values.map(v => Math.max(0, finite(v)));
  const max = Math.max(finite(scaleMax), ...clean);
  if (max <= 0 || track === 0) {
    return values.map(() => ({x: 0, width: 0}));
  }
  const drawn = clean.filter(v => v > 0).length;
  const total = clean.reduce((sum, v) => sum + v, 0);
  const gaps = Math.max(0, drawn - 1) * gap;
  // The whole bar's length is to scale; the gaps come out of it.
  const length = Math.min(track, Math.max(total / max * track, drawn * minWidth + gaps));
  const room = Math.max(0, length - gaps);

  // A segment whose share is under minWidth gets minWidth; the others share what's left in
  // proportion. Taking that room from them can push another under it, hence the loop.
  const small = new Set();
  for (;;) {
    const bigTotal = clean.reduce((sum, v, i) => (small.has(i) ? sum : sum + v), 0);
    const bigRoom = Math.max(0, room - small.size * minWidth);
    const under = clean.findIndex((v, i) => v > 0 && !small.has(i) && v / bigTotal * bigRoom < minWidth);
    if (bigTotal <= 0 || under < 0) {
      break;
    }
    small.add(under);
  }
  const bigTotal = clean.reduce((sum, v, i) => (small.has(i) ? sum : sum + v), 0);
  const bigRoom = Math.max(0, room - small.size * minWidth);

  let x = 0;
  let seen = 0;
  return clean.map((value, i) => {
    if (value === 0) {
      return {x, width: 0};
    }
    seen++;
    const share = small.has(i) || bigTotal <= 0 ? minWidth : value / bigTotal * bigRoom;
    // The end clamp is only a safety net (a track narrower than the segments' minimums).
    const w = Math.min(share, Math.max(0, length - x));
    const segment = {x, width: w};
    x = Math.min(length, x + w + (seen < drawn ? gap : 0));
    return segment;
  });
}
